/**
 * Eine Rueckmeldung ansehen, fuer H&K (Rolle verwaltung).
 *
 * Aufgebaut wie die Dokumentseite: links der Beleg, rechts die Analyse. Die
 * Analyse ist die der Person, die die Rueckmeldung geschrieben hat, und zwar
 * so, wie sie sie beim Senden vor sich hatte; sie wird seither vielleicht neu
 * erstellt oder bearbeitet worden sein. Lesen, nicht bearbeiten.
 *
 * Rechts daneben die Rueckmeldungen dieser Person zu diesem Dokument, ein- und
 * ausklappbar ueber den Knopf oben rechts. Wer eine aeltere anklickt, sieht in
 * der Mitte die Analyse von damals.
 */
import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAnmeldung } from '../lib/anmeldung'
import { Markdown } from '../lib/markdown'
import { Geteilt } from '../teile/Geteilt'

type Urteil = 'passt' | 'ueberfluessig' | 'falsch' | 'fehlt' | 'anmerkung'

interface Rueckmeldung {
  id: string
  text: string
  gesprochen: boolean
  stichpunkte: { punkt: string | null; urteil: Urteil; text: string }[] | null
  zusammenfassung: 'offen' | 'fertig' | 'gescheitert'
  erledigt_am: string | null
  erstellt_am: string
  hat_analyse: boolean
  verfasser: string
}

interface Dokument {
  id: string
  bezeichnung: string
  dateiname: string
  mandant_name: string
}

const URTEIL: Record<Urteil, string> = {
  passt: 'passt',
  ueberfluessig: 'überflüssig',
  falsch: 'falsch',
  fehlt: 'fehlt',
  anmerkung: 'Anmerkung',
}

/** Wie auf dem Server (rueckmeldung/routen.ts): danach gilt eine offene Zusammenfassung als haengengeblieben. */
const HAENGT_NACH_MS = 5 * 60_000

function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function Eintrag({ r, gewaehlt, waehlen }: { r: Rueckmeldung; gewaehlt: boolean; waehlen: () => void }) {
  const kunde = useQueryClient()
  const [stichpunkte, setStichpunkte] = useState(false)
  const neu = useMutation({
    mutationFn: () => api.senden(`/api/rueckmeldungen/${r.id}/zusammenfassen`),
    onSettled: () => void kunde.invalidateQueries({ queryKey: ['rueckmeldungen'] }),
  })
  const abhaken = useMutation({
    mutationFn: () => api.aendern(`/api/rueckmeldungen/${r.id}`, { erledigt: !r.erledigt_am }),
    onSettled: () => void kunde.invalidateQueries({ queryKey: ['rueckmeldungen'] }),
  })
  const wirdZusammengefasst = r.zusammenfassung === 'offen' && Date.now() - new Date(r.erstellt_am).getTime() < HAENGT_NACH_MS

  return (
    <article className={`rueckmeldung${gewaehlt ? ' gewaehlt' : ''}${r.erledigt_am ? ' erledigt' : ''}`}>
      <div className="rueckmeldung-kopf">
        <button type="button" className="rueckmeldung-zeit" onClick={waehlen} aria-pressed={gewaehlt} title="Analyse zu dieser Rückmeldung zeigen">
          {zeitpunkt(r.erstellt_am)}
        </button>
        {r.gesprochen && <span className="nebensache">eingesprochen</span>}
        <label className="schalter">
          <input type="checkbox" checked={Boolean(r.erledigt_am)} onChange={() => abhaken.mutate()} disabled={abhaken.isPending} />
          erledigt
        </label>
      </div>

      <p className="rueckmeldung-text">{r.text}</p>

      {stichpunkte && r.stichpunkte && (
        <ul className="stichpunkte">
          {r.stichpunkte.map((s, i) => (
            <li key={i}>
              <span className="marke">{URTEIL[s.urteil]}</span>
              <div>
                {s.punkt && <div className="nebensache">{s.punkt}</div>}
                <div>{s.text}</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="rueckmeldung-verweise">
        {r.zusammenfassung === 'fertig' && (
          <button type="button" className="verweisknopf" onClick={() => setStichpunkte((z) => !z)} aria-expanded={stichpunkte}>
            {stichpunkte ? 'Stichpunkte ausblenden' : 'Stichpunkte je Analysepunkt'}
          </button>
        )}
        {wirdZusammengefasst && <span className="nebensache">Stichpunkte werden erstellt …</span>}
        {(r.zusammenfassung === 'gescheitert' || (r.zusammenfassung === 'offen' && !wirdZusammengefasst)) && (
          <button type="button" className="verweisknopf" onClick={() => neu.mutate()} disabled={neu.isPending}>
            Stichpunkte noch einmal erstellen
          </button>
        )}
      </div>
    </article>
  )
}

export function RueckmeldungSeite() {
  const { werkzeug, dokumentId, von } = useParams()
  const { auskunft } = useAnmeldung()
  const [spalteOffen, setSpalteOffen] = useState(true)
  const [gewaehlt, setGewaehlt] = useState<string | null>(null)

  const verwaltung = auskunft?.benutzer.rolle === 'verwaltung'

  const dokument = useQuery({
    queryKey: ['dokument', dokumentId],
    queryFn: () => api.holen<{ dokument: Dokument }>(`/api/dokumente/${dokumentId}`),
    enabled: verwaltung && Boolean(dokumentId),
  })

  const rueckmeldungen = useQuery({
    queryKey: ['rueckmeldungen', 'dokument', dokumentId, von],
    queryFn: () => api.holen<{ rueckmeldungen: Rueckmeldung[] }>(`/api/rueckmeldungen/dokument/${dokumentId}?von=${von}`),
    enabled: verwaltung && Boolean(dokumentId && von),
  })

  const liste = rueckmeldungen.data?.rueckmeldungen ?? []
  // Ohne Wahl gilt die neueste Rueckmeldung, die eine Analyse mitbringt.
  const aktuell = liste.find((r) => r.id === gewaehlt) ?? liste.find((r) => r.hat_analyse) ?? liste[0]

  const analyse = useQuery({
    queryKey: ['rueckmeldungen', 'analyse', aktuell?.id],
    queryFn: () => api.holen<{ analyse: string }>(`/api/rueckmeldungen/${aktuell!.id}/analyse`),
    enabled: Boolean(aktuell?.hat_analyse),
  })

  const titel = dokument.data?.dokument
  useEffect(() => {
    if (!titel) return
    const vorher = document.title
    document.title = `Rückmeldung — ${titel.bezeichnung} — ${titel.mandant_name}`
    return () => {
      document.title = vorher
    }
  }, [titel])

  if (!verwaltung || !werkzeug) return <Navigate to="/" replace />
  if (dokument.isPending || rueckmeldungen.isPending) return <div className="laedt">Wird geladen …</div>

  if (dokument.isError || rueckmeldungen.isError || !titel) {
    return (
      <div className="betrachter">
        <div className="betrachter-kopf">
          <Link className="zurueck" to={`/${werkzeug}/rueckmeldungen`}>
            ‹ Rückmeldungen
          </Link>
        </div>
        <div className="fehler" style={{ margin: 'var(--space-lg)' }}>
          {(dokument.error ?? rueckmeldungen.error) instanceof Error ? (dokument.error ?? rueckmeldungen.error)!.message : 'Diese Rückmeldung gibt es nicht.'}
        </div>
      </div>
    )
  }

  const verfasser = aktuell?.verfasser ?? ''

  return (
    <div className="betrachter">
      <div className="betrachter-kopf">
        <Link className="zurueck" to={`/${werkzeug}/rueckmeldungen`}>
          ‹ Rückmeldungen
        </Link>

        <div className="betrachter-titel">
          <h1>{titel.bezeichnung}</h1>
          <div className="nebensache">
            {titel.mandant_name} · {titel.dateiname}
          </div>
        </div>

        <button type="button" className="knopf" aria-pressed={spalteOffen} aria-controls="rueckmeldespalte" onClick={() => setSpalteOffen((o) => !o)}>
          {spalteOffen ? 'Rückmeldungen ausblenden' : `Rückmeldungen ${liste.length}`}
        </button>
      </div>

      <Geteilt
        quelle={`/api/dokumente/${titel.id}/datei`}
        titel={titel.bezeichnung}
        daneben={
          spalteOffen && (
            <aside className="rueckmeldespalte" id="rueckmeldespalte" aria-label={`Rückmeldungen von ${verfasser}`}>
              <div className="rueckmeldespalte-kopf">
                <h2>Rückmeldungen</h2>
                <span className="nebensache">von {verfasser}</span>
              </div>
              <div className="rueckmeldespalte-liste">
                {liste.map((r) => (
                  <Eintrag key={r.id} r={r} gewaehlt={r.id === aktuell?.id} waehlen={() => setGewaehlt(r.id)} />
                ))}
              </div>
            </aside>
          )
        }
      >
        <aside className="analysefeld">
          <div className="analysefeld-kopf">
            <div className="analysefeld-titel">
              <h2>Analyse von {verfasser}</h2>
              {aktuell && <span className="nebensache">Stand beim Senden der Rückmeldung vom {zeitpunkt(aktuell.erstellt_am)}</span>}
            </div>
          </div>
          <div className="analysefeld-koerper">
            {!aktuell?.hat_analyse ? (
              <div className="analysefeld-leer">
                <p className="analysefeld-leer-titel">Keine Analyse</p>
                <p className="analysefeld-leer-text">Beim Senden dieser Rückmeldung hatte {verfasser} keine eigene Analyse zu diesem Dokument.</p>
              </div>
            ) : analyse.isPending ? (
              <div className="laedt">Wird geladen …</div>
            ) : analyse.isError ? (
              <div className="fehler">{analyse.error instanceof Error ? analyse.error.message : 'Nicht ladbar.'}</div>
            ) : (
              <article className="analysefeld-text">
                <Markdown text={analyse.data.analyse} />
              </article>
            )}
          </div>
        </aside>
      </Geteilt>
    </div>
  )
}
